import { useCallback, useState } from 'react'
import { Check, Signal, AlertCircle, Flag } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { StatusSelector } from './ui/status-selector'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const getPriorityDotClass = (priority) => {
  if (priority === 'high') return 'bg-destructive'
  if (priority === 'medium') return 'bg-primary'
  return 'bg-muted-foreground/50'
}

const getPriorityIcon = (priority) => {
  if (priority === 'high') return Flag
  if (priority === 'medium') return AlertCircle
  return Signal
}
const PriorityCommandList = ({ priority, handlePriorityChange }) => {
  const handleSelect = useCallback(
    (value) => {
      handlePriorityChange(value)
    },
    [handlePriorityChange],
  )
  return (
    <CommandList>
      <CommandGroup heading="Priorities">
        {['low', 'medium', 'high'].map((p) => {
          const Icon = getPriorityIcon(p)
          return (
            <CommandItem key={p} value={p} onSelect={handleSelect}>
              <Icon
                className={cn('h-3.5 w-3.5 mr-2', getPriorityDotClass(p).replace('bg-', 'text-'))}
              />
              <span className="capitalize">{p}</span>
              <Check
                className={cn('ml-auto h-3.5 w-3.5', priority === p ? 'opacity-100' : 'opacity-0')}
              />
            </CommandItem>
          )
        })}
      </CommandGroup>
    </CommandList>
  )
}

const PriorityPopoverTrigger = ({ priority, isBoard, stopPropagation }) => {
  const Icon = getPriorityIcon(priority)
  return (
    <PopoverTrigger asChild>
      <Button
        variant="secondary"
        className={cn(
          'h-5.5 w-auto gap-1 rounded-full border-0 bg-secondary/30 px-2 py-0 text-[10px] font-bold shadow-none hover:bg-secondary/60 focus:ring-0 transition-colors uppercase tracking-tight',
          isBoard && 'h-5 px-1.5',
        )}
        onPointerDown={stopPropagation}
        onClick={stopPropagation}
      >
        <Icon className={cn('h-3 w-3', getPriorityDotClass(priority).replace('bg-', 'text-'))} />
        {priority}
      </Button>
    </PopoverTrigger>
  )
}
const PriorityPopoverContent = ({ priority, onSelect, stopPropagation }) => (
  <PopoverContent className="p-0 w-[140px]" align="start" onPointerDown={stopPropagation}>
    <Command>
      <PriorityCommandList priority={priority} handlePriorityChange={onSelect} />
    </Command>
  </PopoverContent>
)

export const PrioritySelector = ({
  priority,
  handlePriorityChange,
  isBoard,
  stopPropagation,
  isExpanded,
}) => {
  const [open, setOpen] = useState(false)

  const onSelect = useCallback(
    (val) => {
      handlePriorityChange(val)
      setOpen(false)
    },
    [handlePriorityChange],
  )

  return (
    <div className="flex items-center gap-2.5">
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <Popover open={open} onOpenChange={setOpen} modal={false}>
              <PriorityPopoverTrigger
                priority={priority}
                isBoard={isBoard}
                stopPropagation={stopPropagation}
              />
              <PriorityPopoverContent
                priority={priority}
                onSelect={onSelect}
                stopPropagation={stopPropagation}
              />
            </Popover>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Set priority</p>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}
export const StatusSelectorWrapper = ({
  status,
  existingStatuses,
  handleStatusChange,
  isBoard,
  isExpanded,
}) => {
  return (
    <div className="flex items-center gap-2.5">
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="status-selector-container rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            role="group"
            aria-label="Status Selector"
          >
            <StatusSelector
              status={status}
              existingStatuses={existingStatuses}
              onStatusChange={handleStatusChange}
              size={isBoard ? 'xs' : 'sm'}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Set status</p>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}
export function TodoItemMetaRow({ todo, onUpdate, viewMode = 'list' }) {
  const isBoard = viewMode === 'board'
  const stopPropagation = useCallback((e) => {
    e.stopPropagation()
  }, [])
  const handlePriorityChange = useCallback(
    (value) => {
      onUpdate(todo.id, {
        priority: value,
      })
    },
    [onUpdate, todo.id],
  )
  const handleStatusChange = useCallback(
    (value) => {
      onUpdate(todo.id, {
        status: value,
      })
    },
    [onUpdate, todo.id],
  )
  const existingStatuses = ['todo', 'done']
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2 mb-2 pt-0.5',
        isBoard && 'gap-x-3 gap-y-1.5 mb-2',
      )}
    >
      <StatusSelectorWrapper
        status={todo.status}
        existingStatuses={existingStatuses}
        handleStatusChange={handleStatusChange}
        isBoard={isBoard}
        isExpanded={todo.isExpanded}
      />

      <PrioritySelector
        priority={todo.priority}
        handlePriorityChange={handlePriorityChange}
        isBoard={isBoard}
        stopPropagation={stopPropagation}
        isExpanded={todo.isExpanded}
      />
    </div>
  )
}
